import React from "react";
import { useDocument } from "../loan_tool";
import CurrencyField from "../../fields/currency_field";
import PercentField from "../../fields/percent_field";

// ── Helpers ────────────────────────────────────────────────────────────────────

function num(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function formatDollars(n) {
  if (n === 0) return "$0";
  const abs = Math.round(Math.abs(n));
  const formatted = "$" + abs.toLocaleString("en-US");
  return n < 0 ? "−" + formatted : formatted;
}

function formatPct(n) {
  return n.toFixed(2) + "%";
}

// ── Defaults ───────────────────────────────────────────────────────────────────

const DEFAULT_PAGE4 = {
  p4_seller_agent_pct: "2.5",
  p4_buyer_agent_pct: "2.5",
  p4_escrow_title_pct: "1.25",
  p4_noo_tax_pct: "3.330",
  p4_investor_contribution: "0",
  p4_investor_profit_pct: "9",
  p4_other_costs: "0"
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function ReadOnlyField({ label, value }) {
  return (
    <div className="ff-read-only-field">
      <div className="ff-field-label">{label}</div>
      <div className="ff-field-value">{value || "—"}</div>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="ff-total-row">
      <span className="ff-total-label">{label}</span>
      <span className="ff-total-value">{formatDollars(value)}</span>
    </div>
  );
}

function GrandRow({ label, value }) {
  return (
    <div className="ff-section-total">
      <span>{label}</span>
      <span>{formatDollars(value)}</span>
    </div>
  );
}

function PctRow({ label, pctFieldName, pctValue, dollarResult, onChange, defaultPct }) {
  return (
    <div className="ff-pct-row">
      <span className="ff-total-label" style={{ flex: 1 }}>{label}</span>
      <div className="ff-pct-inputs">
        <PercentField
          name=""
          fieldName={pctFieldName}
          fixedDigits={3}
          value={pctValue}
          onChange={onChange}
        />
        <span style={{ fontSize: "12px", color: "#777", margin: "0 4px" }}>=</span>
        <span className="ff-pct-result">{formatDollars(dollarResult)}</span>
      </div>
    </div>
  );
}

function ReturnCard({ label, value, variant }) {
  const cls = ["ff-return-card", variant ? "ff-return-card-" + variant : ""].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <div className="ff-return-card-label">{label}</div>
      <div className="ff-return-card-value">{typeof value === "string" ? value : formatDollars(value)}</div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function FixAndFlipPage4() {
  const [document, setDocument, { lastSaveDate }, { saveDocument }] = useDocument();

  const fix_and_flip = document.fix_and_flip || {};
  const page1 = fix_and_flip.page1 || {};
  const page2 = fix_and_flip.page2 || {};
  const page3 = fix_and_flip.page3 || {};
  const page4 = { ...DEFAULT_PAGE4, ...fix_and_flip.page4 };

  function updatePage4(updatedFields) {
    setDocument({
      ...document,
      fix_and_flip: {
        ...fix_and_flip,
        page4: { ...page4, ...updatedFields }
      }
    });
  }

  // ── Data from other pages ───────────────────────────────────────────────────
  const arv = num(page1.arv);
  const holdMonths = num(page1.estimated_hold_time) || 1;
  const purchasePrice = num((document.loans && document.loans[0]) ? document.loans[0].offer_price : "0");
  const salesPriceLender = num(page1.accepted_price) || purchasePrice;

  // Col 1 subtotal (Page 2)
  const col1Subtotal = Object.values(page2).reduce((acc, v) => {
    const n = parseFloat(v);
    return acc + (isNaN(n) ? 0 : n);
  }, 0);

  // Col 2 subtotal (Page 3 additional repairs)
  const repairKeys = [
    "ext_fencing","ext_driveway","ext_landscaping","ext_paint","ext_deck","ext_misc",
    "gar_door","gar_floor","gar_walls","gar_electrical","gar_misc",
    "roof_main","roof_gutters","roof_skylights","roof_misc",
    "pool_resurfacing","pool_plumbing","pool_equipment","pool_misc",
    "mech_hvac","mech_panel","mech_plumbing","mech_misc",
    "other_permits","other_clean","other_staging","other_misc1","other_misc2"
  ];
  const col2Subtotal = repairKeys.reduce((acc, k) => acc + num(page3[k]), 0);

  // Financing cost from Page 3 lender section
  const ltv1 = num(page3.lender_ltv1 || "80");
  const ltv2 = num(page3.lender_ltv2 || "0");
  const loan1 = salesPriceLender * ltv1 / 100;
  const loan2 = salesPriceLender * ltv2 / 100;
  const closingCosts = salesPriceLender * num(page3.lender_closing_pct || "3.5") / 100;
  const earnest = num(page3.lender_earnest);
  const rate1 = num(page3.lender_rate1);
  const rate1Type = page3.lender_rate1_type || "IO";
  const rate2 = num(page3.lender_rate2);
  const rate2Type = page3.lender_rate2_type || "IO";

  function calcMoPay(loan, rate, type) {
    if (loan === 0 || rate === 0) return 0;
    const m = rate / 100 / 12;
    if (type === "PI") return loan * m / (1 - Math.pow(1 + m, -360));
    return loan * m;
  }

  const moPay1 = calcMoPay(loan1, rate1, rate1Type);
  const moPay2 = calcMoPay(loan2, rate2, rate2Type);
  const propTaxPerYear = salesPriceLender * num(page1.property_tax_rate || "1.25") / 100;
  const insuranceMo = num(page1.annual_insurance || "1200") / 12;
  const taxMo = propTaxPerYear / 12;

  const financingCost = closingCosts + earnest + (moPay1 + moPay2) * holdMonths + (taxMo + insuranceMo) * holdMonths;

  // Utilities from Page 3
  const utilGas = num(page3.util_gas);
  const utilElectric = num(page3.util_electric);
  const utilWater = num(page3.util_water);
  const utilTrash = num(page3.util_trash);
  const utilTotal = (utilGas + utilElectric + utilWater + utilTrash) * holdMonths;

  // ── Costs to sell ───────────────────────────────────────────────────────────
  const sellerCommission = arv * num(page4.p4_seller_agent_pct) / 100;
  const buyerCommission = arv * num(page4.p4_buyer_agent_pct) / 100;
  const escrowTitle = arv * num(page4.p4_escrow_title_pct) / 100;
  const nooTax = arv * num(page4.p4_noo_tax_pct) / 100;
  const investorContrib = num(page4.p4_investor_contribution);
  const investorProfit = investorContrib * num(page4.p4_investor_profit_pct) / 100;
  const totalInvestorCost = investorContrib + investorProfit;
  const otherCosts = num(page4.p4_other_costs);
  const totalCostsToSell = sellerCommission + buyerCommission + escrowTitle + nooTax + totalInvestorCost + otherCosts;

  // ── Project cost ────────────────────────────────────────────────────────────
  const totalProjectCost = purchasePrice + col1Subtotal + col2Subtotal + financingCost + utilTotal;

  // ── Investor return ─────────────────────────────────────────────────────────
  const netProfit = arv - totalCostsToSell - totalProjectCost;
  const roi = totalProjectCost > 0 ? netProfit / totalProjectCost * 100 : 0;
  const annualizedROI = holdMonths > 0 ? roi / (holdMonths / 12) : 0;
  const profitVariant = netProfit >= 0 ? "profit" : "loss";

  function formatLastSaved() {
    if (!lastSaveDate) return "Not yet saved";
    return "Last saved: " + new Date(lastSaveDate).toLocaleTimeString();
  }

  return (
    <div>
      {/* Sale Information */}
      <div className="ff-section">
        <div className="ff-section-title">Sale Information (auto-filled from Page 1)</div>
        <div className="ff-section-body">
          <ReadOnlyField label="After Repair Value (ARV)" value={formatDollars(arv)} />
          <ReadOnlyField label="Purchase Price" value={formatDollars(purchasePrice)} />
          <ReadOnlyField label="Hold Time" value={holdMonths + " months"} />
        </div>
      </div>

      {/* Costs to Sell */}
      <div className="ff-section">
        <div className="ff-section-title">Costs to Sell</div>

        <PctRow
          label="Seller Agent Commission"
          pctFieldName="p4_seller_agent_pct"
          pctValue={page4.p4_seller_agent_pct}
          dollarResult={sellerCommission}
          onChange={updatePage4}
        />
        <PctRow
          label="Buyer Agent Commission"
          pctFieldName="p4_buyer_agent_pct"
          pctValue={page4.p4_buyer_agent_pct}
          dollarResult={buyerCommission}
          onChange={updatePage4}
        />
        <PctRow
          label="Escrow / Title Fees"
          pctFieldName="p4_escrow_title_pct"
          pctValue={page4.p4_escrow_title_pct}
          dollarResult={escrowTitle}
          onChange={updatePage4}
        />
        <PctRow
          label="State / Federal NOO Tax"
          pctFieldName="p4_noo_tax_pct"
          pctValue={page4.p4_noo_tax_pct}
          dollarResult={nooTax}
          onChange={updatePage4}
        />

        {/* Investor Contribution */}
        <div className="ff-investor-box">
          <div className="ff-investor-title">Investor Contribution</div>
          <CurrencyField
            name="Down Payment / Build Funds Contributed by Investor"
            fieldName="p4_investor_contribution"
            fixedDigits={0}
            value={page4.p4_investor_contribution}
            onChange={updatePage4}
          />
          <PctRow
            label="Investor Profit %"
            pctFieldName="p4_investor_profit_pct"
            pctValue={page4.p4_investor_profit_pct}
            dollarResult={investorProfit}
            onChange={updatePage4}
          />
          <TotalRow label="Total Investor Cost (returned + profit)" value={totalInvestorCost} />
        </div>

        <CurrencyField
          name="Other Costs"
          fieldName="p4_other_costs"
          fixedDigits={0}
          value={page4.p4_other_costs}
          onChange={updatePage4}
        />

        <GrandRow label="Total Costs to Sell" value={totalCostsToSell} />
      </div>

      {/* Utilities */}
      <div className="ff-section">
        <div className="ff-section-title">Utilities During Hold Period (from Page 3)</div>
        <div className="ff-utility-grid">
          <div>
            <div className="ff-utility-label">Gas (monthly)</div>
            <div className="ff-utility-value">{formatDollars(utilGas)}</div>
          </div>
          <TotalRow label={`Gas Total (${holdMonths} mo)`} value={utilGas * holdMonths} />
          <div>
            <div className="ff-utility-label">Electric (monthly)</div>
            <div className="ff-utility-value">{formatDollars(utilElectric)}</div>
          </div>
          <TotalRow label={`Electric Total (${holdMonths} mo)`} value={utilElectric * holdMonths} />
          <div>
            <div className="ff-utility-label">Water (monthly)</div>
            <div className="ff-utility-value">{formatDollars(utilWater)}</div>
          </div>
          <TotalRow label={`Water Total (${holdMonths} mo)`} value={utilWater * holdMonths} />
          <div>
            <div className="ff-utility-label">Trash (monthly)</div>
            <div className="ff-utility-value">{formatDollars(utilTrash)}</div>
          </div>
          <TotalRow label={`Trash Total (${holdMonths} mo)`} value={utilTrash * holdMonths} />
        </div>
        <div style={{ padding: "0 16px 12px" }}>
          <GrandRow label="Total Utilities" value={utilTotal} />
        </div>
      </div>

      {/* Project Cost Summary */}
      <div className="ff-section">
        <div className="ff-section-title">Project Cost Summary (auto-filled)</div>
        <div style={{ padding: "12px 16px" }}>
          <TotalRow label="Purchase Price" value={purchasePrice} />
          <TotalRow label="Interior Repairs (Page 2)" value={col1Subtotal} />
          <TotalRow label="Additional Repairs (Page 3)" value={col2Subtotal} />
          <TotalRow label="Financing & Hold Cost (Page 3)" value={financingCost} />
          <TotalRow label="Utilities" value={utilTotal} />
        </div>
        <div style={{ padding: "0 16px 12px" }}>
          <GrandRow label="Total Project Cost" value={totalProjectCost} />
        </div>
      </div>

      {/* Investor Return Summary */}
      <div className="ff-section">
        <div className="ff-section-title">Investor Return Summary</div>
        <div style={{ padding: "16px" }}>
          <div className="ff-return-card-grid">
            <ReturnCard label="Gross Sale (ARV)" value={arv} />
            <ReturnCard label="Total Costs to Sell" value={totalCostsToSell} />
            <ReturnCard label="Total Project Cost" value={totalProjectCost} />
          </div>
          <div className="ff-return-card-grid">
            <ReturnCard label="Investor Return" value={totalInvestorCost} />
            <ReturnCard label="Net Profit to Builder" value={netProfit} variant={profitVariant} />
            <ReturnCard label="ROI %" value={formatPct(roi)} />
          </div>
          <div className="ff-return-card-grid">
            <ReturnCard label="Annualized ROI %" value={formatPct(annualizedROI)} variant="highlight" />
          </div>
          <p style={{ fontSize: "11px", color: "#777", fontStyle: "italic", marginTop: "8px" }}>
            Net Profit = ARV − Costs to Sell − Total Project Cost. ROI = Net Profit ÷ Total Project Cost. Annualized = ROI ÷ (months ÷ 12).
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="ff-footer">
        <span className="ff-last-saved">{formatLastSaved()}</span>
        <div className="ff-footer-actions">
          <button className="button" onClick={saveDocument}>
            Save to Loan File
          </button>
        </div>
      </div>
    </div>
  );
}
